import {computedAsync} from '@vueuse/core';
import {defineStore, storeToRefs} from 'pinia';
import {useUserStore} from './userStore';
import type {UserMeBookingFor} from '../api/models/user/userMeBookingForModel';
import {getUsersMeBookingForAllowed} from '../api/repositories/booking/BookingForRepository';
import {computed, ref, watch} from 'vue';

export const useBookingForStore = defineStore('bookingFor', () => {
    const {user, isAuthenticated} = storeToRefs(useUserStore());
    const userMeBookingForId = ref<string | null>(user.value?.id ?? null);
    const isGuestBooking = ref<boolean>(false);

    const usersMeBookingForAllowed = computedAsync<UserMeBookingFor[]>(async () => {
        if (!isAuthenticated.value || !user.value?.id) {
            return [];
        }
        await Promise.resolve();
        return await getUsersMeBookingForAllowed();
    }, []);

    watch(user, user => {
        userMeBookingForId.value = user ? user.id : null;
        if (userMeBookingForId.value) {
            isGuestBooking.value = false;
        }
    });

    const isSelfBooking = computed<boolean>(() => {
        return userMeBookingForId.value === user.value?.id;
    });

    const userMeBookingFor = computed(
        () =>
            usersMeBookingForAllowed.value.find(user => user.id === userMeBookingForId.value) ??
            null
    );

    type BookingMode = 'self' | 'guest' | 'other';

    const bookingMode = computed<BookingMode>(() => {
        if (isGuestBooking.value) {
            return 'guest';
        }
        if (isSelfBooking.value) {
            return 'self';
        }
        return 'other';
    });

    function handleUserSelect(userId: string | null) {
        userMeBookingForId.value = userId;
        isGuestBooking.value = false;
    }

    function enterGuestBooking() {
        isGuestBooking.value = true;
        userMeBookingForId.value = user.value?.id ?? null;
    }

    /**
     * The effective id -> only set when the user is not booking for himself
     */
    const effectiveBookingForId = computed(() => userMeBookingFor.value?.id ?? null);

    return {
        userMeBookingForId,
        userMeBookingFor,
        usersMeBookingForAllowed,
        isSelfBooking,
        isGuestBooking,
        handleUserSelect,
        enterGuestBooking,
        bookingMode,
        effectiveBookingForId,
    };
});
